import cx from 'classnames'
import { type Id } from '@/infra/id'
import { type ColumnId } from '@/features/admin/staffing/components/ApplicantManagementTable/applicant-management-table-columns'
import { type WorkspaceApplicantStatusVariant } from '@/features/admin/staffing/workspace-posting-formatters'
import { IconButton } from '@/components/IconButton'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import {
  ClickableCheckboxCell,
  ClickableImageCell,
  ClickableTableCell,
  ClickableTableRow,
  ClickableTextCell,
} from '@/components/Table/ClickableTable'
import { match } from '@/utils/pattern-match-util'
import styles from './ApplicantManagementTableRow.module.css'

export interface ApplicantManagementTableRowColumn {
  id: ColumnId
  align: 'left' | 'center' | 'right'
}

export interface ApplicantManagementTableRowProps {
  columns: ApplicantManagementTableRowColumn[]
  userPublicId: Id
  userDetailsLink: string
  isSelected: boolean
  onToggleSelected: () => void
  displayName: string
  avatar?: string | null
  proManager: string
  proTypeTitle: string
  stateRegion: string
  yearsExperience: number
  totalWorkspaces: number
  displayStatus: string
  statusVariant: WorkspaceApplicantStatusVariant
  canInvite: boolean
  canRescindInvitation: boolean
  onInvite: () => void
  onRescindInvitation: () => void
}

export function ApplicantManagementTableRow({
  columns,
  userPublicId,
  userDetailsLink,
  isSelected,
  onToggleSelected,
  displayName,
  avatar,
  proManager,
  proTypeTitle,
  stateRegion,
  yearsExperience,
  totalWorkspaces,
  displayStatus,
  statusVariant,
  canInvite,
  canRescindInvitation,
  onInvite,
  onRescindInvitation,
}: ApplicantManagementTableRowProps) {
  const testId = 'applicant-manager-applicant'

  return (
    <ClickableTableRow
      data-test={`applicant-manager-row-${userPublicId}`}
      href={userDetailsLink}
      target="_blank"
      rel="noopener noreferrer"
      alwaysShowPointer
    >
      {columns.map((column) => {
        switch (column.id) {
          case 'Checkbox':
            return (
              <ClickableCheckboxCell
                key={column.id}
                checked={isSelected}
                onChange={onToggleSelected}
                testId={`${testId}-select-${userPublicId}`}
              />
            )
          case 'Applicant':
            return (
              <ClickableImageCell
                key={column.id}
                title={displayName}
                image={avatar}
              />
            )
          case 'ContactOwner':
            return (
              <ClickableTextCell key={column.id}>
                {proManager}
              </ClickableTextCell>
            )
          case 'Type':
            return (
              <ClickableTextCell key={column.id}>
                {proTypeTitle}
              </ClickableTextCell>
            )
          case 'Location':
            return (
              <ClickableTextCell alignment={column.align} key={column.id}>
                {stateRegion}
              </ClickableTextCell>
            )
          case 'Experience':
            return (
              <ClickableTextCell alignment={column.align} key={column.id}>
                {yearsExperience}
              </ClickableTextCell>
            )
          case 'TotalWorkspaces':
            return (
              <ClickableTextCell alignment={column.align} key={column.id}>
                {totalWorkspaces}
              </ClickableTextCell>
            )
          case 'Status':
            return (
              <StatusCell
                key={column.id}
                displayStatus={displayStatus}
                variant={statusVariant}
              />
            )
          case 'Action':
            return (
              <ActionCell
                key={column.id}
                userPublicId={userPublicId}
                canInvite={canInvite}
                canRescindInvitation={canRescindInvitation}
                onInvite={onInvite}
                onRescindInvitation={onRescindInvitation}
              />
            )
        }
      })}
    </ClickableTableRow>
  )
}

interface StatusCellProps {
  displayStatus: string
  variant: WorkspaceApplicantStatusVariant
}

function StatusCell({ displayStatus, variant }: StatusCellProps) {
  return (
    <ClickableTableCell
      className={cx(
        styles.statusCell,
        match(variant, {
          Accepted: () => styles.statusJoined,
          Applied: () => styles.statusApplied,
          Expired: () => styles.statusExpired,
          Invited: () => styles.statusInvited,
          Rejected: () => styles.statusDeclined,
          Withdrawn: () => styles.statusWithdrawn,
          Unspecified: () => undefined,
        }),
      )}
    >
      <span>{displayStatus}</span>
    </ClickableTableCell>
  )
}

interface ActionCellProps {
  userPublicId: Id
  canInvite: boolean
  canRescindInvitation: boolean
  onInvite: () => void
  onRescindInvitation: () => void
}

function ActionCell({
  userPublicId,
  canInvite,
  canRescindInvitation,
  onInvite,
  onRescindInvitation,
}: ActionCellProps) {
  const testId = 'applicant-manager-applicant-actions'

  return (
    <ClickableTableCell className={styles.actionCell}>
      <Dropdown
        autoDismiss
        width="miniscule"
        position="bottom-end"
        button={
          <IconButton
            name="more"
            label="Applicant actions"
            testId={`${testId}-open-${userPublicId}`}
            size={24}
            color="var(--grey800)"
          />
        }
      >
        <div>
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem
            onPress={onInvite}
            isDisabled={!canInvite}
            buttonTestId={`${testId}-invite-${userPublicId}`}
          >
            Invite
          </DropdownItem>

          <DropdownItem
            onPress={onRescindInvitation}
            isDisabled={!canRescindInvitation}
            buttonTestId={`${testId}-rescind-invitation-${userPublicId}`}
          >
            Rescind
          </DropdownItem>
        </div>
      </Dropdown>
    </ClickableTableCell>
  )
}
